// ===================================================
// DROPBOX — CORE ENGINE
// ===================================================
const DBX_API = 'https://api.dropboxapi.com/2';
const DBX_CONTENT = 'https://content.dropboxapi.com/2';

function dbxStorageKey(name){const org=window.MarmoriaSaaS?.state.organizationId;return org?`${name}_${org}`:name}
function dbxStorageGet(name){return localStorage.getItem(dbxStorageKey(name))}
function dbxStorageSet(name,value){localStorage.setItem(dbxStorageKey(name),value)}
function dbxStorageRemove(name){localStorage.removeItem(dbxStorageKey(name))}
function dbxGetToken(){ return dbxStorageGet('dbx_access_token')||''; }
function dbxHasRefreshToken(){return !!dbxStorageGet('dbx_refresh_token')}
// Tenta renovar silenciosamente pelo refresh token antes de pedir reconexão —
// evita bloquear o usuário quando ele já conectou e só o access token expirou.
async function dbxEnsureToken(){
  const token=dbxGetToken();
  if(token)return token;
  if(dbxHasRefreshToken()&&await dbxRefreshToken())return dbxGetToken();
  return '';
}
function dbxMigrateLegacyStorage(){
  const org=window.MarmoriaSaaS?.state.organizationId;
  // Os tokens sem sufixo pertencem ao ambiente legado da Bom Sucesso. Nunca
  // reaproveitar essa credencial automaticamente em outra empresa.
  if(org!=='marmoraria-bom-sucesso')return false;
  let migrated=false;
  ['dbx_access_token','dbx_refresh_token'].forEach(name=>{
    const scoped=`${name}_${org}`,legacy=localStorage.getItem(name);
    if(!localStorage.getItem(scoped)&&legacy){localStorage.setItem(scoped,legacy);migrated=true}
  });
  return migrated;
}

async function dbxRefreshToken(){
  const rt = dbxStorageGet('dbx_refresh_token');
  const appKey = dbxGetAppKey()||'bkdhn4mluzgn7ww';
  if(!rt) return false;
  try{
    const resp = await fetch('https://api.dropboxapi.com/oauth2/token',{
      method:'POST',
      headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body: new URLSearchParams({grant_type:'refresh_token',refresh_token:rt,client_id:appKey})
    });
    const data = await resp.json();
    if(data.access_token){
      dbxStorageSet('dbx_access_token', data.access_token);
      if(data.refresh_token) dbxStorageSet('dbx_refresh_token', data.refresh_token);
      sessionStorage.removeItem('dbx_root_resolved');
      return true;
    }
    console.warn('Dropbox refresh recusado',data.error||resp.status);
    if(data.error==='invalid_grant'){dbxStorageRemove('dbx_access_token');dbxStorageRemove('dbx_refresh_token')}
  } catch(e){ console.error('dbxRefreshToken error',e); }
  return false;
}

function dbxGetAppKey(){
  const el = document.getElementById('cfg-dbx-key');
  if(el&&el.value.trim()) return el.value.trim();
  return sessionStorage.getItem('dbx_app_key')||(CACHE.config&&CACHE.config.dbxAppKey)||window.MARMORARIA_DROPBOX_APP_KEY||'';
}

function dbxGetRoot(){
  const resolved=sessionStorage.getItem('dbx_root_resolved');
  if(resolved!==null)return resolved==='__DROPBOX_ROOT__'?'':resolved;
  const el = document.getElementById('cfg-dbx-root');
  let root=(el&&el.value.trim())||sessionStorage.getItem('dbx_root')||(CACHE.config&&CACHE.config.dbxRoot)||'/Marmoraria';
  root=String(root).trim();if(!root)return '/Marmoraria';
  if(!root.startsWith('/'))root='/'+root;
  return root.replace(/\/+$/,'');
}

function dbxToRelativePath(path){
  const root=dbxGetRoot();
  if(!root)return path||'';
  return String(path||'').toLowerCase().startsWith(root.toLowerCase())?String(path).slice(root.length):String(path||'');
}

function dbxStoredPath(value){return typeof value==='string'?value:(value?.path_lower||value?.path_display||'')}

async function dbxCall(endpoint, body=null){
  let token = dbxGetToken();
  if(!token) throw new Error('Sem token Dropbox');
  let resp = await fetch(DBX_API+endpoint, {
    method:'POST',
    headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
    body: body!==null ? JSON.stringify(body) : null
  });
  if(resp.status===401){
    const refreshed = await dbxRefreshToken();
    if(refreshed){
      token = dbxGetToken();
      resp = await fetch(DBX_API+endpoint, {
        method:'POST',
        headers:{'Authorization':'Bearer '+token,'Content-Type':'application/json'},
        body: body!==null ? JSON.stringify(body) : null
      });
      if(resp.status===401){ dbxStorageRemove('dbx_access_token'); initDocumentos(); throw new Error('Sessão expirada'); }
    } else {
      dbxStorageRemove('dbx_access_token');
      initDocumentos();
      throw new Error('Sessão expirada. Reconecte o Dropbox');
    }
  }
  const data=await resp.json().catch(()=>({}));
  if(!resp.ok)throw new Error(data.error_summary||data.error?.['.tag']||data.error_description||`Erro Dropbox ${resp.status}`);
  return data;
}

async function dbxContentFetch(url,options={}){
  let token=dbxGetToken();
  if(!token)throw new Error('Dropbox não conectado');
  const run=()=>fetch(url,{...options,headers:{...(options.headers||{}),Authorization:'Bearer '+token}});
  let resp=await run();
  if(resp.status===401&&await dbxRefreshToken()){token=dbxGetToken();resp=await run()}
  if(!resp.ok){const data=await resp.clone().json().catch(()=>({}));throw new Error(data.error_summary||data.error?.['.tag']||`Erro Dropbox ${resp.status}`)}
  return resp;
}

async function dbxUploadFile(dropboxPath, file, onProgress){
  const token = dbxGetToken();
  if(!token) throw new Error('Sem token Dropbox');
  const root = dbxGetRoot();
  const fullPath = root ? root.replace(/\/$/,'') + dropboxPath : dropboxPath;
  const CHUNK = 8*1024*1024;
  if(file.size <= CHUNK){
    const resp = await dbxContentFetch(DBX_CONTENT+'/files/upload',{
      method:'POST',
      headers:{
        'Content-Type':'application/octet-stream',
        'Dropbox-API-Arg': JSON.stringify({path:fullPath,mode:'overwrite',autorename:true,mute:false})
      },
      body: file
    });
    const result=await resp.json();
    await auditar('criou','Dropbox',`Enviou arquivo ${file.name}`,null,{id:result.id||fullPath,path:fullPath,nome:file.name,tamanho:file.size}).catch(()=>{});
    return result;
  }
  // Chunked upload for large files
  let offset=0, sessionId='';
  const startResp = await dbxContentFetch(DBX_CONTENT+'/files/upload_session/start',{
    method:'POST',
    headers:{'Content-Type':'application/octet-stream','Dropbox-API-Arg':'{"close":false}'},
    body: file.slice(0,CHUNK)
  });
  const startData = await startResp.json();
  sessionId = startData.session_id;
  offset += CHUNK;
  while(offset < file.size){
    const chunk = file.slice(offset, offset+CHUNK);
    const isLast = offset+chunk.size >= file.size;
    const arg = isLast
      ? JSON.stringify({cursor:{session_id:sessionId,offset},commit:{path:fullPath,mode:'overwrite',autorename:true}})
      : JSON.stringify({cursor:{session_id:sessionId,offset}});
    const ep = isLast ? '/files/upload_session/finish' : '/files/upload_session/append_v2';
    const r = await dbxContentFetch(DBX_CONTENT+ep,{
      method:'POST',
      headers:{'Content-Type':'application/octet-stream','Dropbox-API-Arg':arg},
      body: chunk
    });
    if(isLast){const result=await r.json();await auditar('criou','Dropbox',`Enviou arquivo ${file.name}`,null,{id:result.id||fullPath,path:fullPath,nome:file.name,tamanho:file.size}).catch(()=>{});return result}
    offset += chunk.size;
    if(onProgress) onProgress(Math.round(offset/file.size*100));
  }
}

async function dbxGetThumbnailUrl(path){
  path=dbxToRelativePath(dbxStoredPath(path));
  const token = dbxGetToken();
  if(!token||!path) return null;
  const root = dbxGetRoot();
  const fullPath = root ? root.replace(/\/$/,'') + path : path;
  try{
    const resp = await dbxContentFetch('https://content.dropboxapi.com/2/files/get_thumbnail_v2',{
      method:'POST',
      headers:{
        'Dropbox-API-Arg': JSON.stringify({resource:{'.tag':'path',path:fullPath},format:{'.tag':'jpeg'},size:{'.tag':'w256h256'},mode:{'.tag':'fitone_best_fit'}})
      }
    });
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  } catch(e){ return null; }
}

// Versão que retorna data: URL diretamente — usada na proposta (iframe context)
async function dbxGetThumbnailDataUrl(path){
  const _b2d=blob=>new Promise(res=>{const r=new FileReader();r.onload=e=>res(e.target.result);r.readAsDataURL(blob);});
  const normPath=dbxToRelativePath(dbxStoredPath(path));
  const token=dbxGetToken();
  if(!token||!normPath)return null;
  const root=dbxGetRoot();
  const fullPath=root?root.replace(/\/$/,'')+normPath:normPath;
  try{
    const resp=await dbxContentFetch('https://content.dropboxapi.com/2/files/get_thumbnail_v2',{
      method:'POST',
      headers:{'Dropbox-API-Arg':JSON.stringify({resource:{'.tag':'path',path:fullPath},format:{'.tag':'jpeg'},size:{'.tag':'w256h256'},mode:{'.tag':'fitone_best_fit'}})}
    });
    const blob=await resp.blob();
    return await _b2d(blob);
  }catch(e){console.error('[proposta foto]',path,e);return null;}
}

async function dbxGetTempLink(path){
  const root = dbxGetRoot();
  const fullPath = root ? root.replace(/\/$/,'') + path : path;
  const res = await dbxCall('/files/get_temporary_link', {path:fullPath});
  return res.link||null;
}

// ===================================================
// DOCUMENTOS — Navegador Dropbox
// ===================================================
// DOCUMENTOS — Navegador Dropbox
// initDocumentos/dbxListFolder/renderFileList e demais helpers exclusivos da pagina movidos para page-documentos.js (o Dropbox Core Engine acima continua aqui, usado por outras paginas)

